import React, { useCallback, useEffect, useState } from "react";
import { X, History, Search, RefreshCw, Clock, Users, CheckCircle, XCircle } from "lucide-react";
import { CommanServices } from "@/services/commanServices";
import { formatDateTime } from "@/uitilites/utility";

export default function DutyCheckHistoryModal({
    isOpen,
    onClose,
    onSelectItem,
    selectedId,
    liveCheckId,
}) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");

    const fetchHistory = useCallback(async (query) => {
        setLoading(true);
        try {
            const res = await CommanServices.getDutyCheckHistory({
                limit: 100,
                q: (query ?? search).trim() || undefined,
            });
            if (res?.status) {
                setItems(res.data?.items || []);
            } else {
                setItems([]);
            }
        } catch (e) {
            console.error(e);
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [search]);

    useEffect(() => {
        if (!isOpen) return;
        setSearch("");
        fetchHistory("");
    }, [isOpen, fetchHistory]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden border border-gray-100 max-h-[85vh] flex flex-col">
                <div className="bg-[#0a1a3f] p-5 text-white flex justify-between items-center shrink-0">
                    <div className="flex items-center gap-2">
                        <History className="w-5 h-5" />
                        <h2 className="text-lg font-bold">Status Check History</h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 hover:bg-white/10 rounded-full transition-colors"
                        aria-label="Close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-4 border-b border-gray-100 flex gap-2 shrink-0">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="search"
                            placeholder="Search by sender, type, or ID…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && fetchHistory()}
                            className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-indigo-500/20"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={fetchHistory}
                        disabled={loading}
                        className="px-3 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 text-gray-600"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                    </button>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-2">
                    {loading && items.length === 0 ? (
                        <div className="flex items-center justify-center py-16 text-gray-400 gap-2">
                            <RefreshCw className="w-5 h-5 animate-spin" />
                            Loading history…
                        </div>
                    ) : items.length === 0 ? (
                        <div className="text-center py-16 text-gray-400">
                            <History className="w-10 h-10 mx-auto mb-3 opacity-30" />
                            <p className="text-gray-500 font-medium">No status check history yet</p>
                        </div>
                    ) : (
                        items.map((item) => {
                            const isScheduled = item.kind === "scheduled";
                            const rowId = isScheduled ? item.duty_check_id || item.id : item.id;
                            const isSelected =
                                !isScheduled &&
                                (selectedId === item.id || (liveCheckId && item.id === liveCheckId && !selectedId));
                            const stats = item.stats;
                            const canLoad = !isScheduled && item.id;

                            return (
                                <button
                                    key={`${item.kind}-${item.id}`}
                                    type="button"
                                    disabled={!canLoad}
                                    onClick={() => {
                                        if (canLoad) {
                                            onSelectItem(item);
                                            onClose();
                                        }
                                    }}
                                    className={`w-full text-left p-4 rounded-2xl border transition-all ${isSelected
                                        ? "border-indigo-500 bg-indigo-50 ring-1 ring-indigo-200"
                                        : "border-gray-100 bg-white hover:border-indigo-200 hover:bg-gray-50"
                                        } ${!canLoad ? "opacity-80 cursor-default" : ""}`}
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0 flex-1">
                                            <p className="font-semibold text-gray-900 text-sm truncate">
                                                {item.title || item.message}
                                            </p>
                                            <p className="text-xs text-gray-500 mt-1">
                                                {isScheduled
                                                    ? `Scheduled: ${formatDateTime(item.scheduled_at)}`
                                                    : `Sent: ${formatDateTime(item.created_at)}`}
                                                {" · "}
                                                {item.sent_by_name}
                                            </p>
                                            <p className="text-xs text-indigo-600 mt-1 font-medium">
                                                {item.target_type_label}
                                                {item.target_count != null
                                                    ? ` · ${item.target_count} recipients`
                                                    : ""}
                                            </p>
                                        </div>
                                        {item.id === liveCheckId && !isScheduled && (
                                            <span className="shrink-0 text-[10px] uppercase font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                                                Latest
                                            </span>
                                        )}
                                    </div>
                                    {stats && (
                                        <div className="flex flex-wrap gap-2 mt-3">
                                            <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-slate-100 text-slate-600">
                                                <Users className="w-3 h-3" />
                                                {stats.total} total
                                            </span>
                                            <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700">
                                                <CheckCircle className="w-3 h-3" />
                                                {stats.in_duty} in duty
                                            </span>
                                            <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-red-50 text-red-700">
                                                <XCircle className="w-3 h-3" />
                                                {stats.not_in_duty} not in duty
                                            </span>
                                            <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-amber-50 text-amber-700">
                                                <Clock className="w-3 h-3" />
                                                {stats.pending} pending
                                            </span>
                                        </div>
                                    )}
                                    {isScheduled && (
                                        <p className="text-[11px] text-amber-700 mt-2">
                                            Awaiting scheduled send — open after it runs.
                                        </p>
                                    )}
                                </button>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
}
